import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TIMEOUT_MINUTES = 30;
const TIMEOUT_MS = TIMEOUT_MINUTES * 60 * 1000;

const SessionTimeout = ({ children, onTimeout }) => {
    const navigate = useNavigate();
    const [showWarning, setShowWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(60);

    // Define logout function first since it's used by other functions
    const logout = useCallback(() => {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        sessionStorage.removeItem('savi_chat_messages');
        sessionStorage.removeItem('savi_chat_token');
        navigate('/', { replace: true });
    }, [navigate]);

    // Define resetTimer function
    const resetTimer = useCallback(() => {
        if (window.timeoutId) clearTimeout(window.timeoutId);
        if (window.warningId) clearTimeout(window.warningId);

        // Set warning timeout (1 minute before logout)
        window.warningId = setTimeout(() => {
            setShowWarning(true);
            startCountdown();
        }, (TIMEOUT_MINUTES - 1) * 60 * 1000);

        // Set logout timeout
        window.timeoutId = setTimeout(() => {
            // Clear sensitive data from sessionStorage
            sessionStorage.clear();
            // Optional: Call a logout handler if provided
            if (onTimeout) onTimeout();
            // Redirect to login page
            navigate('/login');
        }, TIMEOUT_MS);
    }, [onTimeout, navigate]);

    const startCountdown = useCallback(() => {
        let seconds = 60;
        const countdownInterval = setInterval(() => {
            seconds -= 1;
            setTimeLeft(seconds);
            if (seconds <= 0) {
                clearInterval(countdownInterval);
            }
        }, 1000);
        
        // Store interval ID for cleanup
        window.countdownIntervalId = countdownInterval;
        
        return () => clearInterval(countdownInterval);
    }, []);

    const stayLoggedIn = useCallback(() => {
        setShowWarning(false);
        setTimeLeft(60);
        if (window.countdownIntervalId) {
            clearInterval(window.countdownIntervalId);
        }
        resetTimer();
    }, [resetTimer]);

    useEffect(() => {
        if (!localStorage.getItem('isAuthenticated')) return;

        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        
        const handleUserActivity = () => {
            resetTimer();
        };

        events.forEach(event => {
            document.addEventListener(event, handleUserActivity);
        });

        resetTimer();

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleUserActivity);
            });
            if (window.timeoutId) clearTimeout(window.timeoutId);
            if (window.warningId) clearTimeout(window.warningId);
            if (window.countdownIntervalId) clearInterval(window.countdownIntervalId);
        };
    }, [resetTimer]);

    // Make functions available globally
    useEffect(() => {
        window.sessionTimeout = {
            stayLoggedIn,
            logout,
            showWarning,
            timeLeft
        };
    }, [stayLoggedIn, logout, showWarning, timeLeft]);

    return children;
};

export default SessionTimeout;